import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const FactorialCalculator = () => {
  const [inputData, setInputData] = useState({
    number: '',
    method: 'iterative'
  });

  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateFactorial = () => {
    const { number, method } = inputData;

    // Validate input
    if (!number) {
      setError('Please enter a non-negative integer');
      return;
    }

    const num = parseInt(number);
    if (isNaN(num) || num < 0) {
      setError('Please enter a valid non-negative integer');
      return;
    }

    let factorial = 1;
    let calculationSteps = [];

    if (method === 'iterative') {
      // Iterative method
      calculationSteps.push(`Step 1: Initialize factorial = 1`);
      for (let i = 1; i <= num; i++) {
        factorial *= i;
        calculationSteps.push(`Step ${i + 1}: factorial = ${factorial} (after multiplying by ${i})`);
      }
    } else {
      // Recursive method (simulated with iteration for display)
      calculationSteps.push(`Step 1: factorial(${num})`);
      let current = num;
      while (current > 1) {
        calculationSteps.push(`Step ${calculationSteps.length + 1}: factorial(${current}) = ${current} * factorial(${current - 1})`);
        current--;
      }
      calculationSteps.push(`Step ${calculationSteps.length + 1}: factorial(1) = 1 (base case)`);
      factorial = calculateRecursiveFactorial(num);
    }

    // Create visualization data
    const chartData = {
      labels: ['Factorial Result'],
      datasets: [{
        label: 'Factorial Value',
        data: [factorial],
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1
      }]
    };

    setResult({
      number: num,
      factorial,
      method,
      calculationSteps,
      chartData
    });
  };

  // Actual recursive calculation (for validation)
  const calculateRecursiveFactorial = (n) => {
    if (n <= 1) return 1;
    return n * calculateRecursiveFactorial(n - 1);
  };

  const exampleData = {
    number: 5,
    factorial: 120
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-green-100 to-lime-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Factorial Calculator | Calculate Factorials</title>
          <meta name="description" content="Calculate factorials of non-negative integers using either iterative or recursive methods. This calculator provides accurate results and detailed calculation steps for educational purposes." />
          <meta name="keywords" content="factorial calculator, calculate factorial, math calculator, factorial calculation, recursive factorial, iterative factorial" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-green-600 mr-4">🔢</div>
            <h1 className="text-3xl font-bold text-gray-800">Factorial Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Calculate the factorial of a non-negative integer using either iterative or recursive methods. The factorial of a number n (denoted as n!) is the product of all positive integers less than or equal to n.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Enter a non-negative integer:</label>
              <input
                type="number"
                name="number"
                value={inputData.number}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter a number (e.g., 5)"
                min="0"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Calculation Method:</label>
              <select
                name="method"
                value={inputData.method}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="iterative">Iterative Method</option>
                <option value="recursive">Recursive Method</option>
              </select>
            </div>
          </div>

          <button
            onClick={calculateFactorial}
            className="w-full py-3 px-4 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate Factorial
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {result && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Factorial Calculation</h3>
                  <p className="text-lg">Number: {result.number}</p>
                  <p className="text-lg">Method: {result.method === 'iterative' ? 'Iterative' : 'Recursive'}</p>
                  <p className="text-2xl font-bold text-green-600 mt-4">{result.number}! = {result.factorial}</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Factorial Visualization</h3>
                  <div className="h-48 bg-white rounded-lg flex items-center justify-center">
                    <svg width="200" height="200" viewBox="0 0 200 200">
                      <rect 
                        x="50" 
                        y="50" 
                        width={`${result.number * 10}`} 
                        height="100" 
                        stroke="green" 
                        strokeWidth="2" 
                        fill="none"
                      />
                      <text x="100" y="40" fontSize="14" textAnchor="middle">n = {result.number}</text>
                    </svg>
                  </div>
                </div>
              </div>

              {result.chartData && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <Bar
                    data={result.chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Factorial Value'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Result'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Factorial Calculation'
                        }
                      }
                    }}
                  />
                </div>
              )}

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Steps</h3>
                <div className="space-y-4">
                  {result.calculationSteps.map((step, index) => (
                    <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                      <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-green-600">📝</span></h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for 5!:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Number: {exampleData.number}</li>
                <li>Factorial: {exampleData.factorial}</li>
              </ul>

              <h4 className="mb-4">Calculation Method:</h4>
              <ol className="list-decimal pl-6 mb-4">
                <li>Start with factorial = 1</li>
                <li>Multiply by 1: 1 × 1 = 1</li>
                <li>Multiply by 2: 1 × 2 = 2</li>
                <li>Multiply by 3: 2 × 3 = 6</li>
                <li>Multiply by 4: 6 × 4 = 24</li>
                <li>Multiply by 5: 24 × 5 = 120</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use <span className="text-green-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Factorial Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Enter a Non-negative Integer:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input the number for which you want to calculate the factorial</li>
                  <li>Ensure the number is a non-negative integer (0 or greater)</li>
                </ul>
              </li>
              <li><strong>Select Calculation Method:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Choose between iterative or recursive calculation methods</li>
                </ul>
              </li>
              <li><strong>Click Calculate:</strong> The calculator will determine:
                <ul className="list-disc pl-6 mt-2">
                  <li>The factorial value of the input number</li>
                  <li>A visual representation of the calculation</li>
                  <li>Detailed calculation steps</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-green-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>0! is defined as 1 by convention</li>
                <li>Factorials grow extremely quickly - even moderate numbers result in very large values</li>
                <li>The recursive method is elegant but can be inefficient for large numbers</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Factorials <span className="text-green-600">💻</span></h2>
          <div className="text-gray-600">
            <p>
              The factorial of a non-negative integer n, denoted by n!, is the product of all positive integers less than or equal to n:
            </p>
            <p className="font-bold text-xl mt-4">n! = n × (n-1) × (n-2) × ... × 3 × 2 × 1</p>
            <p className="mt-2">
              Special cases:
              <ul className="list-disc pl-6 mt-4">
                <li>0! = 1 (by convention)</li>
                <li>1! = 1</li>
              </ul>
            </p>
            <p className="mt-4">
              Factorials have numerous applications in:
              <ul className="list-disc pl-6 mt-4">
                <li><strong>Combinatorics:</strong> Calculating permutations and combinations</li>
                <li><strong>Probability:</strong> Determining possible outcomes</li>
                <strong>Computer Science:</strong> Algorithm analysis and recursion examples
                <li><strong>Statistics:</strong> Various statistical distributions</li>
              </ul>
            </p>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Best Practices <span className="text-green-600">✨</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Use Iterative Method for Large Numbers:</strong> More efficient and avoids recursion depth issues</li>
              <li><strong>Be Mindful of Large Results:</strong> Factorials grow extremely quickly and can exceed standard number limits</li>
              <li><strong>Understand Limitations:</strong> JavaScript can accurately represent integers up to 2^53-1</li>
              <li><strong>Use for Combinatorial Problems:</strong> Factorials are fundamental in counting problems</li>
            </ul>
            <p className="italic mt-4">
              Note: For very large numbers, consider using specialized mathematical software or libraries that support arbitrary-precision arithmetic.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default FactorialCalculator;